import { User, Tv, Clock, Plus } from 'lucide-react';
import type { Seat, FamilyMember } from '@lib/streamFlowApi';

interface FamilySidebarProps {
  members: FamilyMember[];
  seats: Seat[];
  currentMemberId: string | null;
  onSelectMember: (id: string) => void;
  onAddMember: () => void;
}

export default function FamilySidebar({ members, seats, currentMemberId, onSelectMember, onAddMember }: FamilySidebarProps) {
  const getMemberSeat = (memberId: string) =>
    seats.find(s => s.claimed_by === memberId && s.status !== 'available');

  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-sm">Family</h3>
        <button onClick={onAddMember} className="p-1 hover:bg-gray-100 rounded">
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-3">
        {members.map(member => {
          const seat = getMemberSeat(member.id);
          const isSelected = member.id === currentMemberId;

          return (
            <button
              key={member.id}
              onClick={() => onSelectMember(member.id)}
              className={`w-full flex items-start gap-3 p-2 rounded-lg text-left transition-colors ${
                isSelected ? 'bg-slate-100 ring-1 ring-slate-300' : 'hover:bg-gray-50'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                seat ? 'bg-blue-500' : 'bg-gray-300'
              }`}>
                {member.display_name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{member.display_name}</p>
                {seat ? (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Tv className="w-3 h-3 text-blue-500" />
                    <span className="text-xs text-blue-600 truncate">
                      {seat.current_channel_name || 'Watching'}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">Idle</span>
                )}
                {seat?.is_checkout && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3 text-amber-500" />
                    <span className="text-xs text-amber-600">Checked out</span>
                  </div>
                )}
              </div>
              {member.family_role === 'admin' && (
                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Admin</span>
              )}
            </button>
          );
        })}

        {members.length === 0 && (
          <div className="text-center py-4">
            <User className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-400">No family members yet</p>
            <button onClick={onAddMember} className="text-xs text-blue-600 mt-1 hover:underline">
              Add your first member
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
